import {ComparatorChain} from "../../common/comparator-chain";
import {
    Address,
    Category,
    Connection,
    ContractData,
    Location,
    LocationInfo,
    Meter,
    MeterDetails,
    MeterType,
    Organisation,
    OrganisationInfo,
    Role,
    Tag,
    UserProfile
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {toTitleCase} from "../../common/utils";
import {TranslateDirective} from "../../common/utils/translate.directive";
import {ContractEntity} from "../../handlers/app-entity-handler.service";
import {Entity} from "../../handlers/entity";

export type MeterMetadataField = 'tenant' | 'floor' | 'tower' | 'wing';
export type MeterMetadataSource = 'info' | 'details' | null;

export interface ResolvedMeterMetadata {
    field: MeterMetadataField;
    value: string;
    source: MeterMetadataSource;
}

export class RefdataUtils {

    static organisationComparator: ComparatorChain = new ComparatorChain('info.name', 'organisationId');
    static locationsComparator: ComparatorChain = new ComparatorChain('location.info.name', 'location.info.address.street', 'location.locationId');
    static connectionsComparator: ComparatorChain = new ComparatorChain('connection.info.code', 'connection.info.connectionType', 'location.info.name', 'location.info.address.street');
    static reportsComparator: ComparatorChain = new ComparatorChain('info.name', 'reportId');
    static locationEnergyCompassYearsComparator: ComparatorChain = new ComparatorChain('year');
    static alertsComparator: ComparatorChain = new ComparatorChain('alertStatusPriority', '-timestamp',
        'meter.connection.info.code', 'alertId');
    static errorsComparator: ComparatorChain = new ComparatorChain( '-lastSeen', 'errorId');
    static enumFormatter: (value: string) => string = v => toTitleCase(v).replace(/_/g, " ");

    static authorisationOptions: string[] = [Role.owner, Role.manager, Role.viewer];

    static organisationFormatter = (organisation: Organisation) =>
        organisation ? this.organisationInfoFormatter(organisation.info) : null;

    static contractsFormatter = (contract: ContractEntity) =>
        contract ? this.contractInfoFormatter(contract.contract.contractData) : null;

    static organisationInfoFormatter = (organisationInfo: OrganisationInfo) => organisationInfo?.name;

    static contractInfoFormatter = (contractData: ContractData) => contractData?.name;

    static locationInfoFormatter = (info: LocationInfo) => info ?
        `${info.name ? info.name + ' - ' : ''}${(this.addressFormatter(info.address))}` : null;

    static locationEntityFormatter = (loc: Entity) => loc ? this.locationsFormatter(loc.location) : null;

    static locationsFormatter = (loc: Location) => loc ?
        `${loc.info.name ? loc.info.name + ' - ' : ''} ${(this.addressFormatter(loc.info.address))}` : null;

    static addressFormatter = (address: Address, includeZipCode: boolean = true) => address
        ? includeZipCode
            ? `${address.street} ${address.number}${address.addition ? ' ' + address.addition : ''}, ${address.zipCode} ${address.city}`
            : `${address.street} ${address.number}${address.addition ? ' ' + address.addition : ''}, ${address.city}`
        : "";

    static connectionFormatter = (connection: Connection) =>
        (connection.meters?.length > 0 &&
            connection.meters.every(m => m.info.type === MeterType.INTERMEDIATE || (m.info.type === MeterType.PRIMARY && m.info.virtual)) &&
            connection.info.label)
            ? connection.info.label
            : `${connection.info.code}${connection.info.label ? ` (${connection.info.label})` : ''}`;

    static userFormatter = (user: UserProfile) => user ? `${user.info.firstName + ' ' + user.info.lastName}` : null;
    static meterFormatter = (m: Entity) => {
        const withMetadata = (name: string): string => {
            const metadata = this.meterMetadataSummary(m.meter);
            return metadata ? `${name} - ${metadata}` : name;
        };
        if (m.meter.info?.label || m.meter.details?.label) {
            return withMetadata(m.meter.details?.label || m.meter.info?.label);
        }
        const meterType = [MeterType.GROSS_PRODUCTION, MeterType.COOLING].includes(m.meter.info?.type)
            ? m.meter.info.type : m.meter.details?.type || m.meter.info?.type;
        const meterNumbers = (m.meter.details?.meterNumbers || m.meter.info?.meterNumbers || [])
            .filter(meterNumber => !!meterNumber);
        if (meterType === MeterType.INTERMEDIATE
            && (m.meter.info.surveyor?.name === 'Infinity Monitoring' || m.meter.meterId?.startsWith('skyspark-'))
            && meterNumbers.length > 0) {
            return withMetadata(meterNumbers.join(', '));
        }
        const meterCode = m.meter.info.accessPointId;
        if (m.connection.info.connectionType === 'ChargePoint') {
            return withMetadata(meterCode);
        }
        const surveyor = m.meter.info.surveyor?.name;
        return withMetadata(`${m.connection && m.connection.info.code !== meterCode ? m.connection.info.code + ' – ' : ''}${meterCode} (${surveyor === 'EDSN' ? 'KV' : surveyor})`.trim());
    }

    static meterMetadataSummary = (meter: Meter): string => {
        const metadataValues = (field: MeterMetadataField): string[] =>
            (this.getMeterMetadataValue(meter, field) || '').split(';')
                .map(value => value.trim())
                .filter(value => !!value);
        const labeledValues = (field: Exclude<MeterMetadataField, 'tenant'>): string[] =>
            metadataValues(field).map(value => `${TranslateDirective.getTranslation(field, true)} ${value}`);
        const values = [
            ...metadataValues('tenant'),
            ...labeledValues('floor'),
            ...labeledValues('tower'),
            ...labeledValues('wing')
        ];
        return Array.from(new Set(values)).join(' - ');
    }

    static getLocationArea = (location: Location): number => [
        location?.info?.area,
        location?.refdata?.thermalZoneArea,
        location?.refdata?.area
    ].find(area => typeof area === 'number' && area > 1);
    static getMeterArea = (meter: Meter): number => meter?.details?.area;
    static getMeterTags = (meter: Meter): Tag[] => [
        ...(meter?.info?.tags || []),
        ...(meter?.details?.tags || [])
    ].filter((tag, index, tags) => tags.findIndex(candidate =>
        candidate.category === tag.category
        && candidate.customType === tag.customType
        && candidate.value === tag.value) === index);

    static resolveMeterMetadata = (meter: Meter, field: MeterMetadataField): ResolvedMeterMetadata => {
        const sourceValues = this.getTagValues(meter?.info?.tags, field);
        if (sourceValues.length) {
            return {field, value: sourceValues.join('; '), source: 'info'};
        }
        const detailValues = this.getTagValues(meter?.details?.tags, field);
        if (detailValues.length) {
            return {field, value: detailValues.join('; '), source: 'details'};
        }
        return {field, value: '', source: null};
    };

    static getMeterMetadataValue = (meter: Meter, field: MeterMetadataField): string =>
        this.resolveMeterMetadata(meter, field).value;

    static hasSourceMeterMetadataValue = (meter: Meter, field: MeterMetadataField): boolean =>
        this.resolveMeterMetadata(meter, field).source === 'info';

    static setMeterMetadataDetailTag(details: MeterDetails, field: MeterMetadataField, value: string): void {
        details.tags = (details.tags || []).filter(tag => !this.isMeterMetadataTag(tag, field));
        const trimmedValue = value?.trim();
        if (!trimmedValue) {
            return;
        }
        details.tags.push(this.createMeterMetadataDetailTag(field, trimmedValue));
    }

    static isMeterMetadataTag(tag: Tag, field: MeterMetadataField): boolean {
        if (!tag) {
            return false;
        }
        const category = normalizeTagText(tag.category);
        const customType = normalizeTagText(tag.customType);
        switch (field) {
            case 'tenant':
                return category === 'tenant' || category === 'custom' && ['huurder', 'tenant'].includes(customType);
            case 'floor':
                return category === 'floor' || category === 'custom' && ['floor', 'etage', 'verdieping'].includes(customType);
            case 'tower':
                return category === 'tower' || category === 'custom' && ['tower', 'toren'].includes(customType);
            case 'wing':
                return category === 'custom' && ['wing', 'vleugel'].includes(customType);
        }
    }

    private static getTagValues(tags: Tag[] | null | undefined, field: MeterMetadataField): string[] {
        return (tags || [])
            .filter(tag => this.isMeterMetadataTag(tag, field))
            .map(tag => tag.value?.trim())
            .filter(Boolean)
            .filter((value, index, values) => values.indexOf(value) === index);
    }

    private static createMeterMetadataDetailTag(field: MeterMetadataField, value: string): Tag {
        if (field === 'wing') {
            return {category: Category.custom, customType: 'wing', value};
        }
        return {category: field as Category, customType: '', value};
    }
}

function normalizeTagText(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
}
